import { useApi } from '../../lib/use-api'
import apiClient from '../../lib/api-client'

export interface Payment {
  id: number
  sender_name: string | null
  receiver_name: string | null
  memo: string | null
  amount_hnt: number
  payment_method: string | null
  contact_id: number | null
  created_at: string | null
  // R6/MP — neue Serializer-Felder (Api::V2::PaymentsController#serialize_payment).
  // Optional typisiert, damit aeltere Backend-Staende nicht brechen.
  currency?: string | null
  currency_amount?: number | null
  /**
   * true = manuell erfasst (tx_hash-Praefix "manual_" oder payer+payee leer).
   * false = importiertes Blockchain-Payment: editier-/loeschbar (v1-Paritaet),
   * kommt aber beim naechsten Reimport zurueck.
   */
  manual?: boolean
}

interface PagyMeta {
  page: number
  per_page: number
  count: number
  pages: number
}

interface PaymentsListResponse {
  data: Payment[]
  meta: {
    pagy: PagyMeta
  }
}

interface UsePaymentsOptions {
  page: number
  perPage: number
  from: string
  to: string
}

export function usePayments({ page, perPage, from, to }: UsePaymentsOptions) {
  const query = useApi<PaymentsListResponse>(
    ['payments', page, perPage, from, to],
    async () => {
      // FIN-SWEEP-FE-2 (F10): route via api-client so Demo-Token-aware
      // Authorization header is set automatically (lib/api-client.ts:75-102).
      // /api/v2/payments is not in @heliumtracker/api-types yet, hence the
      // `as any` cast (same pattern as useInvoices.ts:57-60).
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const client = apiClient as any

      const queryParams: Record<string, string | number> = {
        page,
        per_page: perPage,
      }
      if (from) queryParams.from = from
      if (to) queryParams.to = to

      // api-client.ts:128 throws ApiError for >=400 (except 401 which is refreshed).
      // So `client.GET` either resolves with { data } or throws an ApiError carrying
      // { status, body } — the `result.error` branch below is only reachable for
      // 401-after-refresh-fail or other openapi-fetch client errors. The thrown
      // `result.error` is always an object, never a string.
      const result = await client.GET('/api/v2/payments', {
        params: { query: queryParams },
      })

      if (result.error) {
        throw new Error(result.error?.message ?? 'HTTP-Fehler beim Laden der Zahlungen')
      }

      const raw = result.data as PaymentsListResponse | undefined
      if (!raw) {
        throw new Error('Keine Daten erhalten')
      }
      return { data: raw }
    },
  )

  // FIN-SWEEP-FE-2 (F2): mirror useInvoices.ts:92-95 — 403 = premium-gated,
  // not a connection error. Expose isPremiumError so the page can suppress
  // the red error UI / skip the toast.
  const isPremiumError =
    query.isError &&
    !!query.error &&
    (query.error as { status?: number }).status === 403

  return {
    data: query.data?.data ?? [],
    isLoading: query.isLoading,
    isError: query.isError && !isPremiumError,
    isPremiumError,
    totalPages: query.data?.meta?.pagy?.pages ?? 1,
    refetch: () => query.refetch(),
  }
}
